// dashboard/components/Profile.jsx
import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  IconButton,
  TextField,
  Divider,
  Chip,
  Grid,
  Paper,
  Dialog,
  DialogContent,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Zoom,
  Grow,
  LinearProgress,
  InputAdornment,
} from "@mui/material";

import {
  CameraAlt,
  Edit,
  Save,
  Close,
  Visibility,
  School,
  AccessTime,
  EmojiEvents,
  MenuBook,
  TrendingUp,
  Telegram,
  Security,
  Email,
  Phone,
  Person,
  CalendarMonth,
  Lock,
  CheckCircle,
  ArrowForward,
  PhotoCamera,
  Cancel,
} from "@mui/icons-material";

import { styled } from "@mui/material/styles";

import { useAuth } from "../../context/authContext";
import api from "../../api/axios";


// ============================================================
// CONSTANTS
// ============================================================

const PRIMARY_DARK = "#001f3f";
const SECONDARY_DARK = "#002a5c";
const PRIMARY_BLUE = "#4a6cf7";
const GREEN = "#2ecc71";
const TEXT_DARK = "#1a1a2e";
const TEXT_MUTED = "#8a8a9a";
const BORDER = "#eef2f7";


// ============================================================
// STYLED COMPONENTS
// ============================================================

const ProfileStatCard = styled(Card)(({ accent }) => ({
  borderRadius: 16,
  border: `1px solid ${BORDER}`,
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  overflow: "hidden",
  position: "relative",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: accent || PRIMARY_BLUE,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },

  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: `0 14px 30px ${
      accent ? `${accent}25` : "rgba(74,108,247,0.15)"
    }`,

    "&::before": {
      opacity: 1,
    },
  },
}));


const InfoRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "14px 0",
});


const SectionCard = styled(Card)({
  borderRadius: 16,
  border: `1px solid ${BORDER}`,
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  overflow: "hidden",
});


// ============================================================
// PROFILE COMPONENT
// ============================================================

const Profile = () => {
  const { user, updateUser } = useAuth();

  const fileInputRef = useRef(null);

  // ==========================================================
  // EDIT PROFILE STATE
  // ==========================================================

  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  const [isSaving, setIsSaving] = useState(false);


  // ==========================================================
  // PROFILE PHOTO STATE
  // ==========================================================

  const [profilePhoto, setProfilePhoto] = useState(null);

  const [photoPreview, setPhotoPreview] = useState(null);

  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);


  // ==========================================================
  // UI STATE
  // ==========================================================

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [snackbarSeverity, setSnackbarSeverity] = useState("success");


  // ==========================================================
  // INITIALIZE USER DATA
  // ==========================================================

  useEffect(() => {
    if (!user) return;

    setFormData({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      phone: user.phone || user.phone_number || "",
    });

    const existingPhoto =
      user.profilePhoto ||
      user.profile_photo ||
      user.avatar ||
      user.avatar_url ||
      null;

    setProfilePhoto(existingPhoto);
  }, [user]);


  // ==========================================================
  // GET USER INITIALS
  // ==========================================================

  const getUserInitials = () => {
    if (!user) return "U";

    const firstName = user.first_name || "";

    const lastName = user.last_name || "";

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }

    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }

    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }

    return "U";
  };


  // ==========================================================
  // GET PROFILE PHOTO
  // ==========================================================

  const getProfilePhoto = () => {
    if (photoPreview) {
      return photoPreview;
    }

    if (profilePhoto) {
      return profilePhoto;
    }

    if (user?.profilePhoto) {
      return user.profilePhoto;
    }

    if (user?.profile_photo) {
      return user.profile_photo;
    }

    if (user?.avatar) {
      return user.avatar;
    }

    if (user?.avatar_url) {
      return user.avatar_url;
    }

    return null;
  };


  // ==========================================================
  // SNACKBAR
  // ==========================================================

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);

    setSnackbarSeverity(severity);

    setSnackbarOpen(true);
  };


  // ==========================================================
  // HANDLE INPUT CHANGE
  // ==========================================================

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };


  // ==========================================================
  // FILE TO BASE64
  // ==========================================================

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => resolve(reader.result);

      reader.onerror = (error) => reject(error);
    });
  };


  // ==========================================================
  // OPEN FILE SELECTOR
  // ==========================================================

  const handleChangePhoto = () => {
    if (isUploadingPhoto) return;

    fileInputRef.current?.click();
  };


  // ==========================================================
  // PROFILE PHOTO CHANGE
  // ==========================================================

  const handleProfilePhotoChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;


    // --------------------------------------------------------
    // VALIDATE TYPE
    // --------------------------------------------------------

    if (!file.type.startsWith("image/")) {
      showSnackbar(
        "Please select a valid image file.",
        "error"
      );

      return;
    }


    // --------------------------------------------------------
    // VALIDATE SIZE
    // --------------------------------------------------------

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar(
        "Image is too large. Maximum size is 5MB.",
        "error"
      );

      return;
    }


    try {
      setIsUploadingPhoto(true);


      // ------------------------------------------------------
      // LOCAL PREVIEW
      // ------------------------------------------------------

      const localPreview = URL.createObjectURL(file);

      setPhotoPreview(localPreview);


      // ------------------------------------------------------
      // CONVERT TO BASE64
      // ------------------------------------------------------

      const base64Image = await fileToBase64(file);


      // ------------------------------------------------------
      // SEND TO BACKEND
      // ------------------------------------------------------

      const response = await api.put(
        "/users/profile/photo",
        {
          profilePhoto: base64Image,
        }
      );


      // ------------------------------------------------------
      // GET UPDATED PHOTO
      // ------------------------------------------------------

      const responseData = response?.data;


      const updatedPhoto =
        responseData?.data?.profilePhoto ||
        responseData?.profilePhoto ||
        responseData?.data?.profile_photo ||
        responseData?.profile_photo ||
        responseData?.user?.profilePhoto ||
        responseData?.user?.profile_photo ||
        base64Image;


      // ------------------------------------------------------
      // UPDATE LOCAL STATE
      // ------------------------------------------------------

      setProfilePhoto(updatedPhoto);

      setPhotoPreview(null);


      // ------------------------------------------------------
      // UPDATE AUTH CONTEXT
      // ------------------------------------------------------

      if (
        updateUser &&
        typeof updateUser === "function"
      ) {
        updateUser({
          profilePhoto: updatedPhoto,
          profile_photo: updatedPhoto,
        });
      }


      showSnackbar(
        "Profile picture updated successfully!",
        "success"
      );


    } catch (error) {
      console.error(
        "Profile picture upload error:",
        error
      );


      setPhotoPreview(null);


      showSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update profile picture.",
        "error"
      );


    } finally {
      setIsUploadingPhoto(false);


      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };


  // ==========================================================
  // SAVE PROFILE - FIXED: Use correct endpoint /users/me
  // ==========================================================

  const handleSaveProfile = async () => {
    if (!formData.first_name.trim()) {
      showSnackbar(
        "First name is required.",
        "error"
      );

      return;
    }


    if (!formData.last_name.trim()) {
      showSnackbar(
        "Last name is required.",
        "error"
      );

      return;
    }


    try {
      setIsSaving(true);


      // ------------------------------------------------------
      // FIXED: Use /users/me instead of /users/profile
      // ------------------------------------------------------

      const response = await api.put(
        "/users/me",
        {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          phone: formData.phone.trim(),
        }
      );


      console.log("Profile update response:", response.data);


      // ------------------------------------------------------
      // GET UPDATED USER DATA
      // ------------------------------------------------------

      const updatedUser = response?.data?.data?.user || response?.data?.user || response?.data;


      // ------------------------------------------------------
      // UPDATE AUTH CONTEXT
      // ------------------------------------------------------

      if (
        updateUser &&
        typeof updateUser === "function"
      ) {
        // Call updateUser to refresh the auth context
        await updateUser({
          first_name: updatedUser?.first_name || formData.first_name.trim(),
          last_name: updatedUser?.last_name || formData.last_name.trim(),
          phone: updatedUser?.phone || formData.phone.trim(),
        });
      }


      // Update local form data with the response
      if (updatedUser) {
        setFormData({
          first_name: updatedUser.first_name || formData.first_name,
          last_name: updatedUser.last_name || formData.last_name,
          email: updatedUser.email || formData.email,
          phone: updatedUser.phone || formData.phone,
        });
      }


      setIsEditing(false);


      showSnackbar(
        "Profile updated successfully!",
        "success"
      );


    } catch (error) {
      console.error(
        "Profile update error:",
        error
      );


      showSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update profile.",
        "error"
      );


    } finally {
      setIsSaving(false);
    }
  };


  // ==========================================================
  // CANCEL EDIT
  // ==========================================================

  const handleCancelEdit = () => {
    setFormData({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      phone:
        user?.phone ||
        user?.phone_number ||
        "",
    });

    setIsEditing(false);
  };


  // ==========================================================
  // VIEW PROFILE PHOTO
  // ==========================================================

  const handleViewPhoto = () => {
    if (!getProfilePhoto()) return;

    setPhotoViewerOpen(true);
  };


  // ==========================================================
  // ACCOUNT CREATED DATE
  // ==========================================================

  const getJoinedDate = () => {
    const date =
      user?.created_at ||
      user?.createdAt ||
      user?.date_joined;

    if (!date) {
      return "Member";
    }

    try {
      return new Date(date).toLocaleDateString(
        "en-US",
        {
          month: "long",
          year: "numeric",
        }
      );
    } catch {
      return "Member";
    }
  };


  // ==========================================================
  // DISPLAY NAME
  // ==========================================================

  const displayName =
    `${user?.first_name || ""} ${
      user?.last_name || ""
    }`.trim() || "Forex Trader";


  // ==========================================================
  // PROFILE COMPLETION
  // ==========================================================

  const calculateProfileCompletion = () => {
    let completed = 0;

    const total = 4;

    if (user?.first_name) completed++;

    if (user?.last_name) completed++;

    if (user?.email) completed++;

    if (
      user?.phone ||
      user?.phone_number
    ) {
      completed++;
    }

    return Math.round(
      (completed / total) * 100
    );
  };


  const profileCompletion =
    calculateProfileCompletion();


  // ==========================================================
  // STATS
  // ==========================================================

  const profileStats = [
    {
      label: "Courses Completed",
      value: "3",
      icon: <School />,
      color: PRIMARY_BLUE,
      bg: "rgba(74,108,247,0.08)",
    },

    {
      label: "Learning Hours",
      value: "21h 30m",
      icon: <AccessTime />,
      color: "#f5a623",
      bg: "rgba(245,166,35,0.08)",
    },

    {
      label: "Certificates",
      value: "2",
      icon: <EmojiEvents />,
      color: "#9b59b6",
      bg: "rgba(155,89,182,0.08)",
    },

    {
      label: "Courses Enrolled",
      value: "19",
      icon: <MenuBook />,
      color: GREEN,
      bg: "rgba(46,204,113,0.08)",
    },
  ];


  // ==========================================================
  // RETURN UI
  // ==========================================================

  return (
    <Box
      sx={{
        width: "100%",
        pb: 5,
      }}
    >

      {/* =====================================================
          PROFILE HERO
      ====================================================== */}

      <Grow in timeout={500}>
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            mb: 4,
            borderRadius: 3,
            background:
              `linear-gradient(
                135deg,
                ${PRIMARY_DARK} 0%,
                ${SECONDARY_DARK} 100%
              )`,
            minHeight: {
              xs: 330,
              md: 280,
            },
            color: "#fff",
          }}
        >

          {/* Decorative circles */}

          <Box
            sx={{
              position: "absolute",
              width: 250,
              height: 250,
              borderRadius: "50%",
              right: -80,
              top: -100,
              background:
                "rgba(74,108,247,0.16)",
              filter: "blur(2px)",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              width: 180,
              height: 180,
              borderRadius: "50%",
              left: "45%",
              bottom: -120,
              background:
                "rgba(46,204,113,0.10)",
            }}
          />


          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              height: "100%",
              minHeight: {
                xs: 330,
                md: 280,
              },
              p: {
                xs: 3,
                md: 4,
              },
              display: "flex",
              alignItems: "center",
              gap: {
                xs: 3,
                md: 5,
              },
              flexDirection: {
                xs: "column",
                md: "row",
              },
              textAlign: {
                xs: "center",
                md: "left",
              },
            }}
          >

            {/* =================================================
                PROFILE PICTURE
            ================================================== */}

            <Zoom in timeout={700}>
              <Box
                sx={{
                  position: "relative",
                  width: {
                    xs: 125,
                    md: 145,
                  },
                  height: {
                    xs: 125,
                    md: 145,
                  },
                  flexShrink: 0,
                }}
              >

                {/* Hidden input */}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={
                    handleProfilePhotoChange
                  }
                />


                {/* Animated ring */}

                <Box
                  onClick={handleViewPhoto}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    padding: "5px",
                    cursor:
                      getProfilePhoto()
                        ? "pointer"
                        : "default",

                    background:
                      "linear-gradient(135deg, #2ecc71, #4a6cf7, #00a8ff)",

                    boxShadow:
                      "0 10px 40px rgba(0,0,0,0.35)",

                    transition:
                      "transform 0.7s cubic-bezier(0.4,0,0.2,1), box-shadow 0.4s ease",

                    "&:hover": {
                      transform:
                        "rotate(360deg)",

                      boxShadow:
                        "0 15px 50px rgba(46,204,113,0.45)",
                    },

                    "&:hover .profile-avatar": {
                      transform:
                        "scale(0.94)",
                    },

                    "&:hover .profile-overlay": {
                      opacity: 1,
                    },
                  }}
                >

                  <Avatar
                    className="profile-avatar"
                    src={
                      getProfilePhoto() ||
                      undefined
                    }
                    alt={`${displayName} profile`}
                    sx={{
                      width: "100%",
                      height: "100%",
                      bgcolor: GREEN,
                      color: "#fff",
                      fontSize: {
                        xs: 42,
                        md: 48,
                      },
                      fontWeight: 800,
                      border:
                        `5px solid ${PRIMARY_DARK}`,
                      transition:
                        "transform 0.5s ease",

                      "& img": {
                        objectFit: "cover",
                      },
                    }}
                  >
                    {!getProfilePhoto() &&
                      getUserInitials()}
                  </Avatar>


                  {/* Hover overlay */}

                  <Box
                    className="profile-overlay"
                    sx={{
                      position: "absolute",
                      inset: 5,
                      borderRadius: "50%",
                      background:
                        "rgba(0,0,0,0.55)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0,
                      transition:
                        "opacity 0.25s ease",
                      pointerEvents: "none",
                    }}
                  >
                    <Visibility
                      sx={{
                        color: "#fff",
                        fontSize: 32,
                      }}
                    />
                  </Box>

                </Box>


                {/* Camera button */}

                <Tooltip title="Change profile picture">
                  <IconButton
                    onClick={
                      handleChangePhoto
                    }
                    disabled={
                      isUploadingPhoto
                    }
                    sx={{
                      position: "absolute",
                      right: -4,
                      bottom: 3,
                      width: 42,
                      height: 42,
                      backgroundColor: GREEN,
                      color: "#fff",
                      border:
                        `4px solid ${PRIMARY_DARK}`,
                      boxShadow:
                        "0 5px 18px rgba(0,0,0,0.3)",

                      "&:hover": {
                        backgroundColor:
                          "#27ae60",
                        transform:
                          "scale(1.08)",
                      },

                      transition:
                        "all 0.25s ease",
                    }}
                  >
                    {isUploadingPhoto ? (
                      <CircularProgress
                        size={19}
                        sx={{
                          color: "#fff",
                        }}
                      />
                    ) : (
                      <CameraAlt
                        sx={{
                          fontSize: 20,
                        }}
                      />
                    )}
                  </IconButton>
                </Tooltip>

              </Box>
            </Zoom>


            {/* =================================================
                PROFILE INFORMATION
            ================================================== */}

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >

              <Chip
                icon={
                  <CheckCircle
                    sx={{
                      fontSize: 16,
                    }}
                  />
                }
                label="Active Trader"
                size="small"
                sx={{
                  mb: 1.2,
                  color: "#fff",
                  backgroundColor:
                    "rgba(46,204,113,0.18)",
                  border:
                    "1px solid rgba(46,204,113,0.3)",
                  fontWeight: 600,

                  "& .MuiChip-icon": {
                    color: GREEN,
                  },
                }}
              />


              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: {
                    xs: "1.8rem",
                    sm: "2.2rem",
                    md: "2.6rem",
                  },
                  lineHeight: 1.15,
                  mb: 0.8,
                }}
              >
                {displayName}
              </Typography>


              <Typography
                sx={{
                  color:
                    "rgba(255,255,255,0.72)",
                  fontSize: {
                    xs: "0.9rem",
                    md: "1rem",
                  },
                  mb: 2,
                }}
              >
                {user?.email ||
                  "Forex Academy Member"}
              </Typography>


              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: {
                    xs: "center",
                    md: "flex-start",
                  },
                  color:
                    "rgba(255,255,255,0.65)",
                }}
              >
                <CalendarMonth
                  sx={{
                    fontSize: 18,
                  }}
                />

                <Typography
                  variant="body2"
                >
                  Member since{" "}
                  {getJoinedDate()}
                </Typography>
              </Box>

            </Box>


            {/* =================================================
                EDIT BUTTON
            ================================================== */}

            <Box
              sx={{
                alignSelf: {
                  xs: "stretch",
                  md: "center",
                },
              }}
            >
              {!isEditing ? (
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={() =>
                    setIsEditing(true)
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    px: 3,
                    py: 1.3,
                    backgroundColor: "#fff",
                    color: PRIMARY_DARK,

                    "&:hover": {
                      backgroundColor:
                        "#f1f5f9",
                      transform:
                        "translateY(-2px)",
                    },

                    transition:
                      "all 0.25s ease",
                  }}
                >
                  Edit Profile
                </Button>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                  }}
                >

                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={
                      handleCancelEdit
                    }
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      color: "#fff",
                      borderColor:
                        "rgba(255,255,255,0.35)",

                      "&:hover": {
                        borderColor: "#fff",
                        backgroundColor:
                          "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    Cancel
                  </Button>


                  <Button
                    variant="contained"
                    startIcon={
                      isSaving ? (
                        <CircularProgress
                          size={18}
                          sx={{
                            color: "#fff",
                          }}
                        />
                      ) : (
                        <Save />
                      )
                    }
                    onClick={
                      handleSaveProfile
                    }
                    disabled={isSaving}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      backgroundColor:
                        GREEN,

                      "&:hover": {
                        backgroundColor:
                          "#27ae60",
                      },
                    }}
                  >
                    Save
                  </Button>

                </Box>
              )}
            </Box>

          </Box>
        </Paper>
      </Grow>


      {/* =====================================================
          PROFILE COMPLETION
      ====================================================== */}

      <Grow in timeout={650}>
        <Card
          sx={{
            borderRadius: 3,
            border: `1px solid ${BORDER}`,
            mb: 4,
            boxShadow:
              "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          <CardContent
            sx={{
              p: {
                xs: 2.5,
                md: 3,
              },
            }}
          >

            <Box
              sx={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >

              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: TEXT_DARK,
                  }}
                >
                  Profile Completion
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: TEXT_MUTED,
                    mt: 0.3,
                  }}
                >
                  Complete your profile to
                  get the most out of Forex
                  Academy.
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontWeight: 800,
                  color:
                    profileCompletion === 100
                      ? GREEN
                      : PRIMARY_BLUE,
                }}
              >
                {profileCompletion}%
              </Typography>

            </Box>


            <LinearProgress
              variant="determinate"
              value={profileCompletion}
              sx={{
                height: 8,
                borderRadius: 5,
                backgroundColor: "#eef2f7",

                "& .MuiLinearProgress-bar":
                  {
                    borderRadius: 5,
                    background:
                      `linear-gradient(
                        90deg,
                        ${PRIMARY_BLUE},
                        ${GREEN}
                      )`,
                  },
              }}
            />

          </CardContent>
        </Card>
      </Grow>


      {/* =====================================================
          STATISTICS
      ====================================================== */}

      <Grid
        container
        spacing={2.5}
        sx={{ mb: 4 }}
      >

        {profileStats.map(
          (stat, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={stat.label}
            >

              <Grow
                in
                timeout={
                  500 + index * 120
                }
              >
                <div>

                  <ProfileStatCard
                    accent={stat.color}
                  >
                    <CardContent
                      sx={{
                        p: 2.5,
                      }}
                    >

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent:
                            "space-between",
                          alignItems:
                            "center",
                        }}
                      >

                        <Box>

                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                TEXT_MUTED,
                              textTransform:
                                "uppercase",
                              letterSpacing:
                                ".5px",
                              fontWeight: 600,
                            }}
                          >
                            {stat.label}
                          </Typography>

                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 800,
                              color:
                                TEXT_DARK,
                              mt: 0.5,
                            }}
                          >
                            {stat.value}
                          </Typography>

                        </Box>


                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor:
                              stat.bg,
                            color:
                              stat.color,
                            transition:
                              "all .3s ease",

                            "&:hover": {
                              transform:
                                "scale(1.1) rotate(6deg)",
                            },
                          }}
                        >
                          {stat.icon}
                        </Avatar>

                      </Box>

                    </CardContent>
                  </ProfileStatCard>

                </div>
              </Grow>
            </Grid>
          )
        )}

      </Grid>


      {/* =====================================================
          MAIN PROFILE CONTENT
      ====================================================== */}

      <Grid
        container
        spacing={3}
      >

        {/* ===================================================
            LEFT COLUMN
        ==================================================== */}

        <Grid
          item
          xs={12}
          md={7}
        >

          {/* ================================================
              PERSONAL INFORMATION
          ================================================= */}

          <SectionCard sx={{ mb: 3 }}>

            <CardContent
              sx={{
                p: {
                  xs: 2.5,
                  md: 3,
                },
              }}
            >

              <Box
                sx={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >

                <Box>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: TEXT_DARK,
                    }}
                  >
                    Personal Information
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: TEXT_MUTED,
                      mt: 0.3,
                    }}
                  >
                    Manage your personal
                    account information.
                  </Typography>

                </Box>


                {!isEditing && (
                  <IconButton
                    onClick={() =>
                      setIsEditing(true)
                    }
                    sx={{
                      color:
                        PRIMARY_BLUE,
                      backgroundColor:
                        "rgba(74,108,247,0.07)",

                      "&:hover": {
                        backgroundColor:
                          "rgba(74,108,247,0.14)",
                      },
                    }}
                  >
                    <Edit
                      fontSize="small"
                    />
                  </IconButton>
                )}

              </Box>


              <Divider
                sx={{ mb: 1 }}
              />


              {/* ==========================================
                  FORM
              =========================================== */}

              {isEditing ? (

                <Grid
                  container
                  spacing={2}
                  sx={{ mt: 0.5 }}
                >

                  <Grid
                    item
                    xs={12}
                    sm={6}
                  >
                    <TextField
                      fullWidth
                      label="First Name"
                      name="first_name"
                      value={
                        formData.first_name
                      }
                      onChange={
                        handleInputChange
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person
                              sx={{
                                color:
                                  TEXT_MUTED,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>


                  <Grid
                    item
                    xs={12}
                    sm={6}
                  >
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="last_name"
                      value={
                        formData.last_name
                      }
                      onChange={
                        handleInputChange
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person
                              sx={{
                                color:
                                  TEXT_MUTED,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>


                  <Grid
                    item
                    xs={12}
                  >
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      value={
                        formData.email
                      }
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email
                              sx={{
                                color:
                                  TEXT_MUTED,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      helperText="Email address cannot be changed here."
                    />
                  </Grid>


                  <Grid
                    item
                    xs={12}
                  >
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={
                        formData.phone
                      }
                      onChange={
                        handleInputChange
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone
                              sx={{
                                color:
                                  TEXT_MUTED,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                </Grid>

              ) : (

                <Box>

                  <InfoRow>

                    <Avatar
                      sx={{
                        width: 42,
                        height: 42,
                        bgcolor:
                          "rgba(74,108,247,0.08)",
                        color:
                          PRIMARY_BLUE,
                      }}
                    >
                      <Person />
                    </Avatar>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            TEXT_MUTED,
                        }}
                      >
                        Full Name
                      </Typography>

                      <Typography
                        sx={{
                          fontWeight: 600,
                          color:
                            TEXT_DARK,
                        }}
                      >
                        {displayName}
                      </Typography>
                    </Box>

                  </InfoRow>


                  <Divider />


                  <InfoRow>

                    <Avatar
                      sx={{
                        width: 42,
                        height: 42,
                        bgcolor:
                          "rgba(74,108,247,0.08)",
                        color:
                          PRIMARY_BLUE,
                      }}
                    >
                      <Email />
                    </Avatar>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            TEXT_MUTED,
                        }}
                      >
                        Email Address
                      </Typography>

                      <Typography
                        sx={{
                          fontWeight: 600,
                          color:
                            TEXT_DARK,
                          wordBreak:
                            "break-word",
                        }}
                      >
                        {user?.email ||
                          "Not provided"}
                      </Typography>
                    </Box>

                  </InfoRow>


                  <Divider />


                  <InfoRow>

                    <Avatar
                      sx={{
                        width: 42,
                        height: 42,
                        bgcolor:
                          "rgba(46,204,113,0.08)",
                        color:
                          GREEN,
                      }}
                    >
                      <Phone />
                    </Avatar>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            TEXT_MUTED,
                        }}
                      >
                        Phone Number
                      </Typography>

                      <Typography
                        sx={{
                          fontWeight: 600,
                          color:
                            TEXT_DARK,
                        }}
                      >
                        {user?.phone ||
                          user?.phone_number ||
                          "Not provided"}
                      </Typography>
                    </Box>

                  </InfoRow>

                </Box>
              )}

            </CardContent>
          </SectionCard>


          {/* ================================================
              ACCOUNT SECURITY
          ================================================= */}

          <SectionCard>

            <CardContent
              sx={{
                p: {
                  xs: 2.5,
                  md: 3,
                },
              }}
            >

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2,
                }}
              >

                <Avatar
                  sx={{
                    bgcolor:
                      "rgba(74,108,247,0.08)",
                    color:
                      PRIMARY_BLUE,
                  }}
                >
                  <Security />
                </Avatar>

                <Box>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: TEXT_DARK,
                    }}
                  >
                    Account Security
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: TEXT_MUTED,
                    }}
                  >
                    Keep your account
                    protected.
                  </Typography>

                </Box>

              </Box>


              <Divider />


              <Box
                sx={{
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "space-between",
                  gap: 2,
                }}
              >

                <Box
                  sx={{
                    display: "flex",
                    alignItems:
                      "center",
                    gap: 1.5,
                  }}
                >

                  <Lock
                    sx={{
                      color:
                        TEXT_MUTED,
                    }}
                  />

                  <Box>

                    <Typography
                      sx={{
                        fontWeight: 600,
                        color:
                          TEXT_DARK,
                      }}
                    >
                      Password
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          TEXT_MUTED,
                      }}
                    >
                      Last updated recently
                    </Typography>

                  </Box>

                </Box>


                <Button
                  endIcon={
                    <ArrowForward
                      fontSize="small"
                    />
                  }
                  sx={{
                    color:
                      PRIMARY_BLUE,
                    textTransform:
                      "none",
                    fontWeight: 600,
                  }}
                >
                  Change
                </Button>

              </Box>


              <Divider />


              <Box
                sx={{
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "space-between",
                  gap: 2,
                }}
              >

                <Box
                  sx={{
                    display: "flex",
                    alignItems:
                      "center",
                    gap: 1.5,
                  }}
                >

                  <Email
                    sx={{
                      color:
                        TEXT_MUTED,
                    }}
                  />

                  <Box>

                    <Typography
                      sx={{
                        fontWeight: 600,
                        color:
                          TEXT_DARK,
                      }}
                    >
                      Email Verification
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          TEXT_MUTED,
                      }}
                    >
                      Your email address
                      is connected to
                      your account.
                    </Typography>

                  </Box>

                </Box>


                <Chip
                  icon={
                    <CheckCircle
                      sx={{
                        fontSize: 15,
                      }}
                    />
                  }
                  label="Verified"
                  size="small"
                  sx={{
                    color:
                      GREEN,
                    backgroundColor:
                      "rgba(46,204,113,0.08)",
                    fontWeight: 700,

                    "& .MuiChip-icon": {
                      color:
                        GREEN,
                    },
                  }}
                />

              </Box>

            </CardContent>
          </SectionCard>

        </Grid>


        {/* ===================================================
            RIGHT COLUMN
        ==================================================== */}

        <Grid
          item
          xs={12}
          md={5}
        >

          {/* ================================================
              PROFILE PHOTO CARD
          ================================================= */}

          <SectionCard sx={{ mb: 3 }}>

            <CardContent
              sx={{
                p: 3,
                textAlign: "center",
              }}
            >

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: TEXT_DARK,
                  mb: 0.5,
                }}
              >
                Profile Picture
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: TEXT_MUTED,
                  mb: 3,
                }}
              >
                Personalize your Forex
                Academy profile.
              </Typography>


              {/* Large image */}

              <Box
                onClick={handleViewPhoto}
                sx={{
                  position: "relative",
                  width: 155,
                  height: 155,
                  mx: "auto",
                  mb: 3,
                  cursor:
                    getProfilePhoto()
                      ? "pointer"
                      : "default",
                  borderRadius: "50%",
                  padding: "5px",
                  background:
                    "linear-gradient(135deg, #4a6cf7, #2ecc71)",
                  boxShadow:
                    "0 10px 35px rgba(74,108,247,0.2)",
                  transition:
                    "all .6s ease",

                  "&:hover": {
                    transform:
                      "rotate(360deg) scale(1.03)",
                    boxShadow:
                      "0 15px 45px rgba(74,108,247,0.3)",
                  },

                  "&:hover .large-photo-overlay":
                    {
                      opacity: 1,
                    },
                }}
              >

                <Avatar
                  src={
                    getProfilePhoto() ||
                    undefined
                  }
                  sx={{
                    width: "100%",
                    height: "100%",
                    bgcolor: GREEN,
                    border:
                      `5px solid #fff`,
                    fontSize: 52,
                    fontWeight: 800,

                    "& img": {
                      objectFit: "cover",
                    },
                  }}
                >
                  {!getProfilePhoto() &&
                    getUserInitials()}
                </Avatar>


                <Box
                  className="large-photo-overlay"
                  sx={{
                    position: "absolute",
                    inset: 5,
                    borderRadius: "50%",
                    background:
                      "rgba(0,0,0,0.5)",
                    opacity: 0,
                    transition:
                      "opacity .25s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "center",
                    pointerEvents: "none",
                  }}
                >
                  <Visibility
                    sx={{
                      color: "#fff",
                      fontSize: 34,
                    }}
                  />
                </Box>

              </Box>


              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                onClick={
                  handleChangePhoto
                }
                disabled={
                  isUploadingPhoto
                }
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  color:
                    PRIMARY_BLUE,
                  borderColor:
                    "rgba(74,108,247,0.3)",
                  px: 3,

                  "&:hover": {
                    borderColor:
                      PRIMARY_BLUE,
                    backgroundColor:
                      "rgba(74,108,247,0.05)",
                  },
                }}
              >
                {isUploadingPhoto
                  ? "Uploading..."
                  : "Change Picture"}
              </Button>


              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: TEXT_MUTED,
                  mt: 1.5,
                }}
              >
                JPG, PNG or WEBP •
                Maximum 5MB
              </Typography>

            </CardContent>
          </SectionCard>


          {/* ================================================
              SUBSCRIPTION
          ================================================= */}

          <SectionCard sx={{ mb: 3 }}>

            <CardContent
              sx={{
                p: 3,
              }}
            >

              <Box
                sx={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >

                <Box
                  sx={{
                    display: "flex",
                    alignItems:
                      "center",
                    gap: 1.5,
                  }}
                >

                  <Avatar
                    sx={{
                      bgcolor:
                        "rgba(46,204,113,0.08)",
                      color:
                        GREEN,
                    }}
                  >
                    <Telegram />
                  </Avatar>

                  <Box>

                    <Typography
                      sx={{
                        fontWeight: 700,
                        color:
                          TEXT_DARK,
                      }}
                    >
                      Subscription
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          TEXT_MUTED,
                      }}
                    >
                      Your current plan
                    </Typography>

                  </Box>

                </Box>


                <Chip
                  label="Active"
                  size="small"
                  sx={{
                    color:
                      GREEN,
                    backgroundColor:
                      "rgba(46,204,113,0.08)",
                    fontWeight: 700,
                  }}
                />

              </Box>


              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, rgba(74,108,247,0.06), rgba(46,204,113,0.06))",
                  border:
                    "1px solid #eef2f7",
                }}
              >

                <Typography
                  variant="caption"
                  sx={{
                    color: TEXT_MUTED,
                  }}
                >
                  Current Plan
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color:
                      TEXT_DARK,
                  }}
                >
                  Forex Academy
                  Premium
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: TEXT_MUTED,
                    mt: 0.5,
                  }}
                >
                  Full access to
                  premium courses,
                  books and learning
                  materials.
                </Typography>

              </Box>


              <Button
                fullWidth
                endIcon={<ArrowForward />}
                sx={{
                  mt: 2,
                  color:
                    PRIMARY_BLUE,
                  fontWeight: 700,
                  textTransform:
                    "none",
                }}
              >
                Manage Subscription
              </Button>

            </CardContent>
          </SectionCard>


          {/* ================================================
              LEARNING PROGRESS
          ================================================= */}

          <SectionCard>

            <CardContent
              sx={{
                p: 3,
              }}
            >

              <Box
                sx={{
                  display: "flex",
                  alignItems:
                    "center",
                  gap: 1.5,
                  mb: 2.5,
                }}
              >

                <Avatar
                  sx={{
                    bgcolor:
                      "rgba(245,166,35,0.08)",
                    color:
                      "#f5a623",
                  }}
                >
                  <TrendingUp />
                </Avatar>

                <Box>

                  <Typography
                    sx={{
                      fontWeight: 700,
                      color:
                        TEXT_DARK,
                    }}
                  >
                    Learning Progress
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        TEXT_MUTED,
                    }}
                  >
                    Your Academy activity
                  </Typography>

                </Box>

              </Box>


              <Box sx={{ mb: 2.5 }}>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    mb: 0.7,
                  }}
                >

                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color:
                        TEXT_DARK,
                    }}
                  >
                    Overall Progress
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color:
                        PRIMARY_BLUE,
                    }}
                  >
                    68%
                  </Typography>

                </Box>

                <LinearProgress
                  variant="determinate"
                  value={68}
                  sx={{
                    height: 7,
                    borderRadius: 5,
                    backgroundColor:
                      "#eef2f7",

                    "& .MuiLinearProgress-bar":
                      {
                        borderRadius: 5,
                        backgroundColor:
                          PRIMARY_BLUE,
                      },
                  }}
                />

              </Box>


              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(2, 1fr)",
                  gap: 1.5,
                }}
              >

                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor:
                      "#f8fafc",
                    textAlign: "center",
                  }}
                >

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color:
                        TEXT_DARK,
                    }}
                  >
                    19
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        TEXT_MUTED,
                    }}
                  >
                    Enrolled
                  </Typography>

                </Box>


                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor:
                      "#f8fafc",
                    textAlign: "center",
                  }}
                >

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color:
                        GREEN,
                    }}
                  >
                    3
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        TEXT_MUTED,
                    }}
                  >
                    Completed
                  </Typography>

                </Box>

              </Box>

            </CardContent>
          </SectionCard>

        </Grid>

      </Grid>


      {/* =====================================================
          PROFILE PHOTO VIEWER
      ====================================================== */}

      <Dialog
        open={photoViewerOpen}
        onClose={() =>
          setPhotoViewerOpen(false)
        }
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor:
              PRIMARY_DARK,
          },
        }}
      >

        <DialogContent
          sx={{
            p: 1,
            minHeight: 450,
            position: "relative",
            display: "flex",
            justifyContent:
              "center",
            alignItems:
              "center",
          }}
        >

          <IconButton
            onClick={() =>
              setPhotoViewerOpen(false)
            }
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 5,
              color: "#fff",
              backgroundColor:
                "rgba(0,0,0,0.45)",

              "&:hover": {
                backgroundColor:
                  "rgba(0,0,0,0.7)",
              },
            }}
          >
            <Close />
          </IconButton>


          {getProfilePhoto() ? (

            <Box
              component="img"
              src={getProfilePhoto()}
              alt={`${displayName} profile`}
              sx={{
                display: "block",
                width: "100%",
                maxHeight:
                  "75vh",
                objectFit:
                  "contain",
                borderRadius: 2,
              }}
            />

          ) : (

            <Avatar
              sx={{
                width: 200,
                height: 200,
                bgcolor: GREEN,
                color: "#fff",
                fontSize: 72,
                fontWeight: 800,
              }}
            >
              {getUserInitials()}
            </Avatar>

          )}

        </DialogContent>
      </Dialog>


      {/* =====================================================
          SNACKBAR
      ====================================================== */}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() =>
          setSnackbarOpen(false)
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >

        <Alert
          severity={snackbarSeverity}
          variant="filled"
          onClose={() =>
            setSnackbarOpen(false)
          }
          sx={{
            width: "100%",
          }}
        >
          {snackbarMessage}
        </Alert>

      </Snackbar>

    </Box>
  );
};


export default Profile;